import { useState } from "react";
import { LoaderCircle, Send, CheckCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/frontend/components/ui/select";

const serviceOptions = [
  "Document chase automation",
  "Close checklist and prep",
  "Reconciliation",
  "Reporting pack",
  "Finance systems build",
  "Something else",
];

const budgetOptions = [
  "Less than $1,000",
  "$1,000 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+",
];

const channels = [
  { k: "Email", v: "contact@fire-chain.com", href: "mailto:contact@fire-chain.com" },
  { k: "Where", v: "Málaga, Spain · US hours" },
  { k: "Reply", v: "Within one business day" },
];

const socials = [
  { label: "GitHub", href: "https://github.com/Bigbrotha12" },
  { label: "LinkedIn", href: "https://linkedin.com/in/rafaelmendoza64" },
  { label: "X", href: "https://x.com/RafaelM27764900" },
  { label: "Telegram", href: "https://t.me/bigbrotha1" },
];

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState("");
  const [budget, setBudget] = useState("");
  const [preferred, setPreferred] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/contact/public", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message: `Service: ${service}\nBudget: ${budget}\n${preferred ? `Preferred: ${preferred}\n` : ""}${message}`,
        }),
      });

      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({ error: "Failed to send message." }))) as {
          error?: string;
        };
        throw new Error(errBody.error || "Failed to send message.");
      }

      setSuccess(true);
      setName("");
      setEmail("");
      setService("");
      setBudget("");
      setPreferred("");
      setMessage("");
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="t-sec" aria-labelledby="contact-title">
      <div className="t-wrap t-contact__grid">
        <div>
          <div className="t-block-head">
            <h2 id="contact-title" className="t-h2 t-rise">
              Let's talk.
            </h2>
          </div>

          <dl className="t-contact__rows">
            {channels.map((channel) => (
              <div className="t-contact__row t-rise" key={channel.k}>
                <dt className="t-contact__k">{channel.k}</dt>
                <dd className="t-contact__dd">
                  {channel.href ? (
                    <a className="t-contact__v" href={channel.href}>
                      {channel.v}
                    </a>
                  ) : (
                    <span className="t-contact__v">{channel.v}</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>

          <nav className="t-contact__socials t-rise" aria-label="Elsewhere">
            {socials.map((social) => (
              <a key={social.label} className="t-link" href={social.href} target="_blank" rel="noreferrer">
                {social.label}
              </a>
            ))}
          </nav>
        </div>

        <div className="t-rise">
          {success ? (
            <div role="alert" className="t-form__msg">
              <CheckCircle aria-hidden="true" className="t-form__icon" />
              <p>Message sent. Thanks for reaching out — I'll get back to you within one business day.</p>
            </div>
          ) : (
            <form className="t-form" onSubmit={handleSubmit}>
              {error && (
                <p className="t-form__msg t-form__err" role="alert">
                  {error}
                </p>
              )}

              <div className="t-form__row">
                <div className="t-form__field">
                  <label htmlFor="name">Full name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="t-form__field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div className="t-form__row">
                <div className="t-form__field">
                  <label htmlFor="service">Service needed</label>
                  <Select value={service} onValueChange={(v) => setService(v ?? "")} required>
                    <SelectTrigger id="service" className="w-full">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="t-form__field">
                  <label htmlFor="budget">Budget</label>
                  <Select value={budget} onValueChange={(v) => setBudget(v ?? "")}>
                    <SelectTrigger id="budget" className="w-full">
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetOptions.map((option) => (
                        <SelectItem key={option} value={option.toLowerCase()}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="t-form__field">
                <label htmlFor="preferred">Preferred date &amp; time</label>
                <input
                  id="preferred"
                  name="preferred"
                  type="datetime-local"
                  value={preferred}
                  onChange={(e) => setPreferred(e.target.value)}
                />
              </div>

              <div className="t-form__field">
                <label htmlFor="message">What is slowing the close?</label>
                <textarea
                  id="message"
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  placeholder="Tell me about your project, goals and timeline..."
                  required
                />
              </div>

              <p className="t-form__note">
                We use your details only to reply to this enquiry. See our{" "}
                <a href="/privacy-policy">Privacy Policy</a>.
              </p>

              <button type="submit" className="t-btn t-form__submit" disabled={loading}>
                {loading ? (
                  <>
                    <LoaderCircle aria-hidden="true" className="animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    Request appointment
                    <Send aria-hidden="true" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
